// RFC822 .eml-Builder fuer den lokalen Outlook-Versand von Angeboten.
//
// Bewusste Entscheidung (kein SMTP/sendReportEmail): Angebote werden als
// .eml-DATEI mit PDF-Anhang heruntergeladen. Der Header `X-Unsent: 1` sorgt
// dafuer, dass Outlook die Datei als EDITIERBAREN ENTWURF oeffnet – der Nutzer
// prueft, ergaenzt seine Signatur und versendet manuell aus seinem Postfach.
//
// Reine, testbare Funktionen (kein DOM) – nur `downloadEml` fasst das DOM an.

import { istNativeApp } from './nativePlatform';
import { ausgabeFehlerMelden, dateiAusgeben } from './dateiAusgabe';

const CRLF = '\r\n';

/** UTF-8-bewusstes Base64 (funktioniert in Node/vitest via Buffer und im Browser via TextEncoder/btoa). */
export const toBase64Utf8 = (str: string): string => {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(str, 'utf-8').toString('base64');
  }
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

/** Bricht einen (Base64-)String verlustfrei auf 76-Zeichen-Zeilen um (RFC 2045). */
const wrap76 = (s: string): string => {
  const clean = s.replace(/\s+/g, '');
  const lines: string[] = [];
  for (let i = 0; i < clean.length; i += 76) lines.push(clean.slice(i, i + 76));
  return lines.join(CRLF);
};

const isAscii = (s: string): boolean => /^[\x00-\x7F]*$/.test(s);

/**
 * Header-Injection-Schutz: entfernt CR/LF aus Header-Werten (To/Subject), damit
 * keine zusaetzlichen Header (z.B. "Bcc:") eingeschleust werden koennen.
 */
const stripHeaderCrlf = (s: string): string => s.replace(/[\r\n]+/g, '');

/**
 * RFC 2047 "encoded-word" fuer Header-Werte mit Nicht-ASCII-Zeichen (Umlaute).
 * ASCII bleibt unveraendert; sonst `=?UTF-8?B?<base64>?=`.
 */
const encodeHeaderWord = (s: string): string =>
  isAscii(s) ? s : `=?UTF-8?B?${toBase64Utf8(s)}?=`;

export interface QuoteEmlArgs {
  to: string[];
  subject: string;
  bodyText: string;
  attachmentFilename: string;
  attachmentBase64: string;
}

/**
 * Baut eine vollstaendige RFC822-.eml (multipart/mixed) mit Text-Body und
 * PDF-Anhang. Immer CRLF-Zeilenenden. Body und Anhang sind Base64-kodiert.
 */
export function buildQuoteEml(args: QuoteEmlArgs): string {
  const boundary = `----=_OSIA_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  const filenameEnc = encodeHeaderWord(args.attachmentFilename);

  const parts: string[] = [
    // ── Header ──────────────────────────────────────────────────────────────
    `To: ${args.to.map(stripHeaderCrlf).join(', ')}`,
    `Subject: ${encodeHeaderWord(stripHeaderCrlf(args.subject))}`,
    'MIME-Version: 1.0',
    // X-Unsent: 1 -> Outlook oeffnet die Datei als editierbaren ENTWURF.
    'X-Unsent: 1',
    `Content-Type: multipart/mixed; boundary="${boundary}"`,
    '',
    // ── Teil 1: Text (Base64, UTF-8-sicher fuer Umlaute) ────────────────────
    `--${boundary}`,
    'Content-Type: text/plain; charset=utf-8',
    'Content-Transfer-Encoding: base64',
    '',
    wrap76(toBase64Utf8(args.bodyText)),
    // ── Teil 2: PDF-Anhang (Base64, 76-Zeichen-Umbruch) ─────────────────────
    `--${boundary}`,
    `Content-Type: application/pdf; name="${filenameEnc}"`,
    `Content-Disposition: attachment; filename="${filenameEnc}"`,
    'Content-Transfer-Encoding: base64',
    '',
    wrap76(args.attachmentBase64),
    // ── Abschluss ───────────────────────────────────────────────────────────
    `--${boundary}--`,
    '',
  ];

  return parts.join(CRLF);
}

/**
 * Laedt die .eml als Datei herunter (message/rfc822). Doppelklick oeffnet sie
 * in Outlook als Entwurf. DOM-abhaengig – daher hier und nicht in buildQuoteEml.
 */
export function downloadEml(filename: string, emlContent: string): void {
  const blob = new Blob([emlContent], { type: 'message/rfc822' });
  // Android-App: Blob-Downloads kommen in der WebView nicht an → Teilen-Dialog
  // (dort z. B. direkt an die Mail-App). Fehler sichtbar, ohne Browser-Dialog.
  if (istNativeApp()) {
    void dateiAusgeben(blob, filename.toLowerCase().endsWith('.eml') ? filename : `${filename}.eml`).catch(ausgabeFehlerMelden);
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.toLowerCase().endsWith('.eml') ? filename : `${filename}.eml`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Objekt-URL nach dem Klick wieder freigeben.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
